/* WeQuote Dev Board — Ideas board (renderIdeas, faithful port; verify §63).
 * A repository for inspiration OUTSIDE the delivery pipeline; promote moves an
 * idea into Backlog and opens it in Focus. Ideas never appear on the Kanban,
 * roadmap, calendar or suggested order. */
import React, { useEffect, useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import * as M from '../core/mutations'
import { MODULE_OPTS } from '../core/constants'
import * as LV from '../core/live'
import type { Feature } from '../core/types'
import { fmtDate } from '../core/dates'
import { similarCount } from '../core/review'
import { LinksEditor, CommentsPane, SourceLine } from './parts'
import { IcArrowUp, IcArchive, IcBulb } from '../icons'

const IdeaRead: React.FC<{ feat: Feature; all: Feature[] }> = ({ feat, all }) => {
  const { mutate, openSubModal, resolveAddToDev } = useDevBoard()
  const sim = similarCount(feat, all)
  const promote = () => {
    mutate((fs) => LV.promoteIdea(fs, feat.id))
    resolveAddToDev(feat.id) // reuses the resolve navigation: land on the card in Focus
  }
  return (
    <>
      <div className="rv-actions">
        <button className="rv-act add" onClick={promote}><IcArrowUp size={12} /> Move to priority list</button>
        <button className="rv-act arch" onClick={() => openSubModal({ kind: 'archive', id: feat.id })}><IcArchive size={12} /> Archive</button>
      </div>
      <div className="dv-d-top"><div className="dv-d-heading"><div className="dv-d-titlewrap"><h2 className="dv-d-title">{feat.title}</h2></div></div></div>
      <div className="dv-d-pills">
        <span className="rv-tag idea">IDEA</span>
        {sim > 0 && <span className="idea-sim">{sim} similar existing feature{sim === 1 ? '' : 's'} — check before promoting</span>}
      </div>
      <div className="idea-field"><label className="idea-lbl">Title</label><input className="idea-in" defaultValue={feat.title} onBlur={(e) => mutate((fs) => M.upd(fs, feat.id, 'title', e.target.value))} /></div>
      <div className="idea-field"><label className="idea-lbl">Description / notes</label><textarea className="idea-ta" defaultValue={feat.notes} placeholder="What’s the idea? Context, inspiration, rough requirements…" onBlur={(e) => mutate((fs) => M.upd(fs, feat.id, 'notes', e.target.value))} /></div>
      <div className="idea-field"><label className="idea-lbl">Inspiration / links</label><LinksEditor feat={feat} /></div>
      <div className="dv-d-sec"><CommentsPane feat={feat} /><SourceLine feat={feat} /></div>
    </>
  )
}

export const IdeasView: React.FC = () => {
  const { cards, addFeatureCard, currentUserName, showToast } = useDevBoard()
  const items = cards.filter((f) => f.status === 'idea').sort((a, b) => (b.dateAdded || '').localeCompare(a.dateAdded || '') || b.id - a.id)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [notes, setNotes] = useState('')
  const [module_, setModule] = useState('')
  useEffect(() => {
    if (selectedId !== null && !items.some((f) => f.id === selectedId)) setSelectedId(null)
    if (selectedId === null && items.length) setSelectedId(items[0].id)
  }, [items, selectedId])
  const sel = selectedId != null ? items.find((f) => f.id === selectedId) || null : null

  const addIdea = async () => {
    if (!title.trim()) { showToast('Give the idea a title.'); return }
    if (!module_) { showToast('Pick which WeQuote module this idea is for — it\u2019s required.'); return }
    const po = cards.reduce((m, f) => Math.max(m, f.po), 0) + 1
    await addFeatureCard({
      po, title: title.trim(), status: 'idea', notes: notes.trim(), module: module_,
      dateAdded: new Date().toISOString().slice(0, 10),
      source: { type: 'manual', by: currentUserName, ts: new Date().toISOString() },
      requests: [],
    } as Partial<Feature>)
    setModalOpen(false); setTitle(''); setNotes(''); setModule('')
    showToast('Idea captured.')
  }

  return (
    <div id="ideasView" style={{ padding: '16px 24px' }}>
      <div className="rv-topbar">
        <p className="rv-intro">A home for inspiration — URLs, half-formed thoughts, weekend ideas. Promote one to send it into the Backlog.</p>
        <button className="rv-sim-btn" onClick={() => setModalOpen(true)}><IcBulb size={13} /> Add idea</button>
      </div>
      <div className={'dv-wrap' + (sel ? ' has-selection' : '')}>
        <div className="dv-list">
          {items.length === 0
            ? <div className="dv-empty-list">No ideas yet. Capture one with “+ Add idea”.</div>
            : items.map((f) => (
              <div key={f.id} className={'dv-row' + (f.id === selectedId ? ' selected' : '')} onClick={() => setSelectedId(f.id)}>
                <div className="dv-row-top"><span className="rv-tag idea">IDEA</span><span className="dv-row-title">{f.title}</span></div>
                {f.dateAdded && <div className="rv-who">Added {fmtDate(f.dateAdded)}</div>}
                {f.notes && <div className="rv-preview">{f.notes}</div>}
              </div>
            ))}
        </div>
        <div className="dv-detail">{sel && <IdeaRead feat={sel} all={cards} />}</div>
      </div>
      {modalOpen && (
        <div className="overlay open" style={{ zIndex: 130 }} onClick={(e) => { if ((e.target as HTMLElement).classList.contains('overlay')) setModalOpen(false) }}>
          <div className="modal" style={{ width: 460 }}>
            <h2>Add an idea</h2>
            <div className="fg"><label>Title *</label><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. AI quote assistant" /></div>
            <div className="fg"><label>Module *</label><select value={module_} onChange={(e) => setModule(e.target.value)}><option value="">Select a module…</option>{MODULE_OPTS.map((mm) => <option key={mm} value={mm}>{mm}</option>)}</select></div>
            <div className="fg"><label>Notes</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Context, inspiration, links…" /></div>
            <div className="factions"><button className="btn-s" onClick={() => setModalOpen(false)}>Cancel</button><button className="btn-p" onClick={() => { void addIdea() }}>Add idea</button></div>
          </div>
        </div>
      )}
    </div>
  )
}
